import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { companyAtom } from "@/store/company";
import { FuelType, Facility, CombustionFacility } from "@/types";
import { PlusCircle, Trash2, Upload } from "lucide-react";
import { toast } from "react-toastify";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { prepareDisplayDate } from "@/lib/utils";

interface CombustionFormProps {
  combustionId: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}

// FacilityAssignment interface is not used in the code

const formSchema = z.object({
  quantity: z.coerce
    .number()
    .min(0.01, { message: "La cantidad debe ser mayor que 0" }),
  provider: z.string().nullable(),
  invoice_amount: z.coerce.number().min(0),
  invoice: z.string().nullable(),
  start_date: z.string(),
  end_date: z.string().nullable(),
  comments: z.string().nullable(),
  fuel_type: z.coerce.number(),
  document: z.instanceof(File).optional().nullable(),
  facilities: z
    .array(
      z.object({
        facility: z.coerce.number(),
        assigned: z.coerce.number().min(0).max(100),
      }),
    )
    .refine(
      (facilities) => {
        const facilityIds = facilities.map((f) => f.facility);
        return new Set(facilityIds).size === facilityIds.length;
      },
      {
        message: "Each facility can only be assigned once",
        path: ["facilities"],
      },
    )
    .refine(
      (facilities) => {
        // Check if the sum of assigned percentages is 100
        const sum = facilities.reduce((acc, f) => acc + f.assigned, 0);
        return sum === 100;
      },
      {
        message: "The sum of all percentages must be 100%",
        path: ["facilities"],
      },
    ),
});

type FormValues = z.infer<typeof formSchema>;

export function CombustionForm({
  combustionId,
  onSuccess,
  onCancel,
}: CombustionFormProps) {
  const { t } = useTranslation();
  const [company] = useAtom(companyAtom);
  const queryClient = useQueryClient();
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [currentFileType, setCurrentFileType] = useState<string>("");
  const [isImagePreviewError, setIsImagePreviewError] = useState(false);
  const { token } = useAuth();

  // Set up form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      quantity: 0,
      fuel_type: undefined,
      invoice_amount: 0,
      invoice: "",
      start_date: "",
      end_date: "",
      comments: "",
      document: null,
      facilities: [
        {
          facility: undefined,
          assigned: 100,
        },
      ],
    },
  });

  // Reset form when opening in create mode
  useEffect(() => {
    if (!combustionId) {
      form.reset({
        quantity: 0,
        fuel_type: undefined,
        invoice_amount: 0,
        invoice: "",
        start_date: "",
        end_date: "",
        comments: "",
        document: null,
        facilities: [
          {
            facility: undefined,
            assigned: 100,
          },
        ],
      });
    }
  }, [combustionId, form]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "facilities",
  });

  // Consulta para obtener los tipos de combustible
  const watchedStartDate = form.watch("start_date");
  const fuelTypesQuery = useQuery<FuelType[]>({
    queryKey: [
      "combustion-factors/types_for_year",
      watchedStartDate ? new Date(watchedStartDate).getFullYear() : undefined,
    ],
    queryFn: async () => {
      const year = watchedStartDate
        ? new Date(watchedStartDate).getFullYear()
        : undefined;
      if (!year) throw new Error("Year is required to fetch fuel types");

      const response = await fetch(
        getApiUrl(`/combustion-factors/types_for_year/?year=${year}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch fuel types");
      }
      const res = await response.json();
      // Endpoint returns a plain array; be defensive in case of pagination shape
      return Array.isArray(res) ? res : res.results;
    },
    enabled: !!watchedStartDate,
  });

  // Consulta para obtener las instalaciones
  const facilitiesQuery = useQuery<Facility[]>({
    queryKey: ["facilities", company?.id],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(`/facilities/?limit=9999&company=${company?.id}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch facilities");
      }
      const res = await response.json();
      return res.results;
    },
    enabled: !!company?.id,
  });

  // Consulta para obtener los datos de combustión si estamos editando
  const combustionQuery = useQuery({
    queryKey: ["combustion", combustionId],
    queryFn: async () => {
      try {
        const url = getApiUrl(`/combustions/${combustionId}/`);
        console.log("Fetching combustion data from:", url);

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        console.log("Combustion API response status:", response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error(
            `Failed to fetch combustion: ${response.status} ${response.statusText}`,
          );
        }

        const res = await response.json();
        console.log("Combustion API response data:", res);
        return res;
      } catch (error) {
        console.error("Error in combustion query:", error);
        throw error;
      }
    },
    enabled: !!combustionId,
    retry: 1,
  });

  // Debug: Log query states
  console.log("Query states:", {
    fuelTypesLoading: fuelTypesQuery.isLoading,
    facilitiesLoading: facilitiesQuery.isLoading,
    combustionLoading: combustionQuery.isLoading,
    hasCombustionId: !!combustionId,
    combustionError: combustionQuery.error,
    combustionData: combustionQuery.data,
  });

  const isLoading =
    fuelTypesQuery.isLoading ||
    facilitiesQuery.isLoading ||
    (combustionId && combustionQuery.isLoading) ||
    (combustionId && !combustionQuery.data);

  const isError =
    fuelTypesQuery.isError ||
    facilitiesQuery.isError ||
    (combustionId && combustionQuery.isError);

  const fuelTypes = fuelTypesQuery.data;
  const facilities = facilitiesQuery.data;
  const combustion = combustionQuery.data;

  // Main effect for form initialization
  useEffect(() => {
    // Do not wait for fuelTypes to avoid deadlock; start_date set here will trigger fuel types query
    if (combustionId && combustion && facilities) {
      try {
        console.log("Initializing form with data:", {
          combustion,
          fuelTypes,
          facilities,
        });

        // Transform facilities data
        let facilitiesData;
        if (combustion.facilities?.length > 0) {
          facilitiesData = combustion.facilities.map(
            (facility: CombustionFacility) => ({
              facility: facility.facility.id,
              assigned: facility.assigned,
            }),
          );
        } else if (combustion.facility?.id) {
          facilitiesData = [
            {
              facility: combustion.facility.id,
              assigned: combustion.assigned || 100,
            },
          ];
        } else {
          facilitiesData = [{ facility: undefined, assigned: 100 }];
        }

        // Handle fuel type - set directly from combustion without waiting for fuelTypes list
        const fuelTypeId = combustion.fuel_type?.id || combustion.fuel_type;

        // Prepare form values
        const formValues = {
          quantity: combustion.quantity || 0,
          provider: combustion.provider || "",
          invoice_amount: combustion.invoice_amount || 0,
          invoice: combustion.invoice || "",
          start_date: combustion.start_date || "",
          end_date: combustion.end_date || "",
          comments: combustion.comments || "",
          fuel_type: fuelTypeId ?? undefined,
          document: null,
          facilities: facilitiesData,
        };

        console.log("Resetting form with values:", formValues);

        // Reset form with new values
        form.reset(formValues);

        // Handle document
        if (combustion.document_url) {
          // URL already comes with authentication parameters from backend
          console.log("Document URL from backend:", combustion.document_url);
          setDocumentUrl(combustion.document_url);
          // Detect file type from URL extension for proper preview
          const url = combustion.document_url.toLowerCase();
          if (
            url.endsWith(".jpg") ||
            url.endsWith(".jpeg") ||
            url.includes(".jpg?") ||
            url.includes(".jpeg?") ||
            url.endsWith(".png") ||
            url.includes(".png?") ||
            url.endsWith(".webp") ||
            url.includes(".webp?") ||
            url.includes("jpg") ||
            url.includes("jpeg") ||
            url.includes("png")
          ) {
            console.log("Setting image type for preview");
            setCurrentFileType("image/jpeg"); // Default to image type for preview
          } else if (
            url.endsWith(".pdf") ||
            url.includes(".pdf?") ||
            url.includes("pdf")
          ) {
            console.log("Setting PDF type for preview");
            setCurrentFileType("application/pdf");
          } else {
            // Try to detect from content-type or default to image for preview
            console.log(
              "Could not detect file type from URL, defaulting to image",
            );
            setCurrentFileType("image/jpeg");
          }
        }

        setIsImagePreviewError(false);
      } catch (error) {
        console.error("Error initializing form:", error);
      }
    }
  }, [combustion, facilities, combustionId]);

  const createMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const formData = new FormData();
      const { facilities, document, ...restData } = data;

      Object.entries(restData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      if (document instanceof File) {
        formData.append("document", document);
      }

      if (facilities && facilities.length > 0) {
        facilities.forEach((facility, index) => {
          formData.append(
            `facilities[${index}]`,
            JSON.stringify({
              facility: facility.facility,
              assigned: facility.assigned,
            }),
          );
        });
      }

      const response = await fetch(getApiUrl("/combustions/"), {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to create combustion");
      }
      const res = await response.json();
      return res;
    },
    onSuccess: () => {
      toast.success(t("common.success.created"));
      queryClient.invalidateQueries({ queryKey: ["combustion-facilities"] });
      onSuccess();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const formData = new FormData();
      const { facilities, document, ...restData } = data;

      Object.entries(restData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      if (document instanceof File) {
        formData.append("document", document);
      }

      if (facilities && facilities.length > 0) {
        const facilitiesJson = JSON.stringify(facilities);
        console.log("Sending facilities:", facilitiesJson);
        formData.append("facilities", facilitiesJson);
      }

      const response = await fetch(getApiUrl(`/combustions/${combustionId}/`), {
        method: "PATCH",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail:
            "Failed to update combustion and could not parse error response.",
        }));
        throw new Error(errorData.detail || "Failed to update combustion");
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success(t("common.success.updated"));
      queryClient.invalidateQueries({ queryKey: ["combustion-facilities"] });
      onSuccess();
    },
  });

  const onSubmit = (values: FormValues) => {
    // Validate at least one facility
    if (!values.facilities || values.facilities.length === 0) {
      toast.error(t("combustion.error.min_one_facility"));
      return;
    }

    const totalPercentage = values.facilities.reduce(
      (sum, facility) => sum + facility.assigned,
      0,
    );
    if (totalPercentage !== 100) {
      toast.error(t("combustion.error.percentage_sum"));
      return;
    }

    const facilityIds = values.facilities.map((f) => f.facility);
    const uniqueFacilityIds = new Set(facilityIds);
    if (uniqueFacilityIds.size !== facilityIds.length) {
      toast.error(t("combustion.error.duplicate_facility"));
      return;
    }

    if (combustionId) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      let detectedType = file.type || "";
      if (!detectedType && file.name) {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === "jpg" || ext === "jpeg") detectedType = "image/jpeg";
        else if (ext === "png") detectedType = "image/png";
        else if (ext === "webp") detectedType = "image/webp";
        else if (ext === "pdf") detectedType = "application/pdf";
      }

      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/jpg",
        "image/*",
      ];

      const isAllowed = allowedTypes.some((type) => {
        if (type === "image/*") {
          return detectedType.startsWith("image/");
        }
        return detectedType === type;
      });

      if (!isAllowed) {
        toast.error(t("combustion.error.pdf_or_image_only"));
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(t("combustion.error.file_too_large"));
        return;
      }

      if (documentUrl && documentUrl.startsWith("blob:")) {
        URL.revokeObjectURL(documentUrl);
      }

      const newUrl = URL.createObjectURL(file);
      setDocumentUrl(newUrl);
      setCurrentFileType(detectedType);
      setIsImagePreviewError(false);
      form.setValue("document", file, { shouldValidate: true });
    } catch (error) {
      console.error("Error handling file change:", error);
      toast.error(t("common.error.unknown"));
    }
  };

  const addFacility = () => {
    // Calculate remaining percentage
    const currentTotal = form
      .getValues()
      .facilities.reduce((sum, facility) => sum + (facility.assigned || 0), 0);

    const remainingPercentage = Math.max(0, 100 - currentTotal);

    append({
      facility: undefined as unknown as number, // Cast to number to satisfy TypeScript
      assigned: remainingPercentage,
    });
  };

  const handleRemoveFacility = (index: number) => {
    if (fields.length > 1) {
      // Get the percentage of the facility being removed
      const removedPercentage =
        form.getValues().facilities[index].assigned || 0;
      remove(index);

      // Redistribute the percentage to the first facility
      if (removedPercentage > 0 && fields.length > 0) {
        const facilities = form.getValues().facilities;
        const firstFacilityIndex = 0;
        const newPercentage =
          (facilities[firstFacilityIndex].assigned || 0) + removedPercentage;

        form.setValue(
          `facilities.${firstFacilityIndex}.assigned`,
          newPercentage,
        );
      }
    } else {
      toast.error(t("combustion.error.min_one_facility"));
    }
  };

  // Function to check if a facility is already selected in another dropdown
  const isFacilitySelected = (facilityId: number, currentIndex: number) => {
    return form
      .getValues()
      .facilities.some(
        (f, idx) => idx !== currentIndex && f.facility === facilityId,
      );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="h-full flex flex-col"
      >
        <div className="flex-1 flex flex-col md:flex-row overflow-y-auto">
          {/* Document Preview/Upload Section */}
          <div className="md:w-1/2 p-4 border-r">
            <div className="flex flex-col h-full">
              <h3 className="text-lg font-medium mb-4">
                {t("combustion.fields.document")}
              </h3>

              {combustionId && combustionQuery.isLoading ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                  <p className="text-muted-foreground">{t("common.loading")}</p>
                </div>
              ) : documentUrl ? (
                <div className="flex flex-col h-full">
                  <div className="flex flex-1 items-center justify-center min-h-[400px] border rounded-md overflow-hidden bg-white">
                    {/* Preview PDF or image */}
                    {(() => {
                      console.log(
                        "Rendering preview with type:",
                        currentFileType,
                        "URL:",
                        documentUrl,
                      );
                      if (currentFileType.startsWith("image/") && documentUrl) {
                        return (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-full h-full flex items-center justify-center">
                              {isImagePreviewError ? (
                                <div className="p-4 text-center">
                                  <p>{t("common.nopreviewload")}</p>
                                  <a
                                    href={documentUrl || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary underline"
                                  >
                                    {t("common.opennewtab")}
                                  </a>
                                </div>
                              ) : (
                                <img
                                  src={documentUrl || ""}
                                  alt={t("combustion.fields.document")}
                                  className="object-contain max-w-full max-h-full"
                                />
                              )}
                            </div>
                          </div>
                        );
                      } else if (documentUrl) {
                        // Default to PDF preview
                        return (
                          <object
                            data={documentUrl}
                            type="application/pdf"
                            width="100%"
                            height="100%"
                            className="min-h-[400px]"
                          >
                            <div className="p-4">
                              <p>{t("combustion.pdf_not_supported")}</p>
                              <a
                                href={documentUrl || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                              >
                                {t("combustion.download_pdf")}
                              </a>
                            </div>
                          </object>
                        );
                      }
                    })()}
                  </div>

                  <div className="mt-4">
                    <label htmlFor="document-upload" className="cursor-pointer">
                      <div className="flex items-center justify-center p-4 border-2 border-dashed rounded-md hover:bg-muted transition-colors">
                        <Upload className="h-6 w-6 mr-2" />
                        <span>{t("combustion.replace_document")}</span>
                      </div>
                      <input
                        id="document-upload"
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col justify-center items-center h-full">
                  <label
                    htmlFor="document-upload"
                    className="cursor-pointer w-full"
                  >
                    <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-md hover:bg-muted transition-colors min-h-[400px]">
                      <Upload className="h-12 w-12 mb-4 text-muted-foreground" />
                      <p className="text-lg font-medium">
                        {t("combustion.upload_document_any")}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {t("combustion.pdf_or_image_only")}
                      </p>
                    </div>
                    <input
                      id="document-upload"
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Form Fields Section */}

          <div className="md:w-1/2 flex-1 flex flex-col space-y-4 overflow-y-auto p-4">
            {/* Agrupar Start Date y End Date en una fila */}
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("combustion.fields.start_date")}</FormLabel>
                    <FormControl>
                      <DatePicker
                        date={
                          field.value
                            ? prepareDisplayDate(field.value)
                            : undefined
                        }
                        setDate={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = date.getMonth();
                            const day = date.getDate();
                            const correctedDate = new Date(
                              Date.UTC(year, month, day),
                            );
                            field.onChange(
                              correctedDate.toISOString().split("T")[0],
                            );
                          } else {
                            field.onChange(null);
                          }
                        }}
                        disableFutureDates={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("combustion.fields.end_date")}</FormLabel>
                    <FormControl>
                      <DatePicker
                        date={
                          field.value
                            ? prepareDisplayDate(field.value)
                            : undefined
                        }
                        setDate={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = date.getMonth();
                            const day = date.getDate();
                            const correctedDate = new Date(
                              Date.UTC(year, month, day),
                            );
                            field.onChange(
                              correctedDate.toISOString().split("T")[0],
                            );
                          } else {
                            field.onChange(null);
                          }
                        }}
                        disableFutureDates={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="fuel_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("combustion.fields.fuel_type")}</FormLabel>
                  <Select
                    value={field.value ? field.value.toString() : undefined}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                  >
                    <FormControl>
                      <SelectTrigger
                        className={
                          !watchedStartDate || isLoading
                            ? "pointer-events-none opacity-60"
                            : undefined
                        }
                      >
                        <SelectValue
                          placeholder={t("combustion.select_fuel_type")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoading ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          {t("common.loading")}...
                        </div>
                      ) : isError ? (
                        <div className="p-2 text-sm text-destructive">
                          {t("common.error_loading_data")}
                        </div>
                      ) : Array.isArray(fuelTypes) ? (
                        fuelTypes.map((fuelType) => (
                          <SelectItem
                            key={fuelType.id}
                            value={fuelType.id.toString()}
                          >
                            {fuelType.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-muted-foreground">
                          {t("common.no_data_available")}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("combustion.fields.quantity")}</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl className="flex-1">
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value))
                        }
                      />
                    </FormControl>
                    {form.watch("fuel_type") && (
                      <div className="text-muted-foreground">
                        {Array.isArray(fuelTypes)
                          ? fuelTypes.find(
                              (ft) => ft.id === form.watch("fuel_type"),
                            )?.unit || ""
                          : ""}
                      </div>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("combustion.fields.provider")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Agrupar Invoice e Invoice Amount en una fila */}
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="invoice"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("combustion.fields.invoice")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="invoice_amount"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t("combustion.fields.invoice_amount")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  {t("combustion.fields.facilities")}
                </h3>
                <Button
                  type="button"
                  variant="gradient"
                  size="sm"
                  onClick={addFacility}
                  className="flex items-center gap-1"
                >
                  <PlusCircle className="h-4 w-4" />
                  {t("combustion.add_facility")}
                </Button>
              </div>

              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex gap-4 items-end border p-3 rounded-md"
                >
                  <div className="flex-grow">
                    <FormField
                      control={form.control}
                      name={`facilities.${index}.facility`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("combustion.fields.facility")}
                          </FormLabel>
                          <Select
                            value={
                              field.value ? field.value.toString() : undefined
                            }
                            onValueChange={(value) =>
                              field.onChange(parseInt(value))
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t("combustion.select_facility")}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Array.isArray(facilities)
                                ? facilities.map((facility) => (
                                    <SelectItem
                                      key={facility.id}
                                      value={facility.id.toString()}
                                      disabled={isFacilitySelected(
                                        facility.id,
                                        index,
                                      )}
                                    >
                                      {facility.name}
                                    </SelectItem>
                                  ))
                                : null}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="w-24">
                    <FormField
                      control={form.control}
                      name={`facilities.${index}.assigned`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("combustion.fields.percentage")}
                          </FormLabel>
                          <div className="flex items-center">
                            <FormControl>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                {...field}
                                onChange={(e) =>
                                  field.onChange(parseInt(e.target.value) || 0)
                                }
                              />
                            </FormControl>
                            <span className="ml-2">%</span>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="self-end"
                    onClick={() => handleRemoveFacility(index)}
                    title={t("common.remove")}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}

              {/* Mostrar errores de validación del array de facilities */}
              {form.formState.errors.facilities && (
                <div className="mt-2 p-2 border border-destructive rounded-md bg-destructive/10">
                  <p className="text-sm font-medium text-destructive">
                    {form.formState.errors.facilities.message ||
                      t("combustion.error.percentage_sum")}
                  </p>
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("combustion.fields.comments")}</FormLabel>
                  <FormControl>
                    <Textarea {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2 border-t bg-white p-4 sticky bottom-0">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="gradient"
            type="submit"
            disabled={form.formState.isSubmitting || isLoading || isError}
          >
            {form.formState.isSubmitting
              ? t("common.saving")
              : t("common.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
